import React, { useState, useEffect } from 'react';
import Layout from '../layout/Layout';
import { PrivateAxios } from '../../request/axios';
import { toast } from 'react-toastify';
import { FaKey, FaEdit, FaUnlock, FaLock, FaUserShield, FaTrash } from 'react-icons/fa';
import Modal from 'react-modal';

const ModifierUtilisateur = () => {
    const [users, setUsers] = useState([]);
    const [intermediaires, setIntermediaires] = useState([]);
    const [roles, setRoles] = useState([])
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [isRolesModalOpen, setIsRolesModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loadingIntermediaires, setLoadingIntermediaires] = useState(true);

    const fetchUsers = async () => {
        try {
            const response = await PrivateAxios.get('/admin/recuperer/user');
            setUsers(response.data || []);
        } catch (err) {
            toast.error('Erreur lors du chargement des utilisateurs.');
        }
    };

    const fetchIntermediaires = async () => {
        try {
            const response = await PrivateAxios.get('/admin/intermediaire');
            setIntermediaires(response.data.int || []);
        } catch (err) {
            toast.error('Erreur lors du chargement des intermédiaires.');
        } finally {
            setLoadingIntermediaires(false);
        }
    };

    const fetchRoles = async () => {
        try {
            const response = await PrivateAxios.get('/admin/role');
            setRoles(response.data.roles)
        } catch (error) {
            toast.error('Erreur lors du chargement des rôles');
        }
    }

    useEffect(() => {
        fetchUsers();
        fetchIntermediaires();
        fetchRoles();
    }, []);

    const filteredUsers = users.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const openEditModal = (user) => {
        setSelectedUser(user);
        setIsModalOpen(true);
    };

    const openPasswordModal = (user) => {
        setSelectedUser(user);
        setNewPassword(''); // Réinitialiser le mot de passe dans la modale
        setIsPasswordModalOpen(true);
    };

    const openRolesModal = (user) => {
        setSelectedUser(user);
        setIsRolesModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
        setIsSubmitting(false);
    };

    const closePasswordModal = () => {
        setIsPasswordModalOpen(false);
        setSelectedUser(null);
        setNewPassword('');
        setIsSubmitting(false);
    };

    const closeRolesModal = () => {
        setIsRolesModalOpen(false);
        setSelectedUser(null);
        fetchUsers();
    };

    const updateUserPassword = async () => {
        if (!selectedUser || !newPassword) {
            toast.error("Le mot de passe ne peut pas être vide.");
            return;
        }

        setIsSubmitting(true);

        const payload = {
            username: selectedUser.username,
            email: selectedUser.email,
            int_id: selectedUser.intermediaire_id,
            password: newPassword
        };

        try {
            await PrivateAxios.put(`/admin/modifier/user/${selectedUser.id}`, payload);
            toast.success('Mot de passe modifié avec succès !');
            fetchUsers();
            closePasswordModal();
        } catch (err) {
            toast.error('Erreur lors de la modification du mot de passe.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const updateUser = async () => {
        if (!selectedUser) return;

        setIsSubmitting(true);
        const payload = {
            username: selectedUser.username,
            email: selectedUser.email,
            int_id: selectedUser.intermediaire_id
        };

        try {
            await PrivateAxios.put(`/admin/modifier/user/${selectedUser.id}`, payload);
            toast.success('Utilisateur modifié avec succès !');
            fetchUsers();
            closeModal();
        } catch (err) {
            toast.error('Erreur lors de la modification de l\'utilisateur.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const toggleUserStatus = async (user) => {
        const newStatus = !user.actif;

        try {
            await PrivateAxios.put(`/admin/desactiver/user/${user.id}`, { actif: newStatus });
            toast.success(`Utilisateur ${user.username} ${newStatus ? 'activé' : 'désactivé'} avec succès.`);
            fetchUsers();
        } catch (err) {
            toast.error(`Erreur lors du changement de statut de ${user.username}.`);
        }
    };

    const handleRoleChange = async (roleId, checked) => {
        const userId = selectedUser.id;

        if (checked) {
            // Ajout du rôle
            try {
                const payload = {
                    'role_id': roleId,
                    'user_id': userId
                };
                await PrivateAxios.post(`/admin/role`, payload);
                toast.success(`Rôle ajouté avec succès.`);

                setSelectedUser((prevUser) => ({
                    ...prevUser,
                    roles: {
                        ...prevUser.roles,
                        [roleId]: { nom: roles.find(role => role.id === roleId).nom }
                    }
                }));
            } catch (error) {
                toast.error('Erreur lors de l\'ajout du rôle.');
            }
        } else {
            // Suppression du rôle
            try {
                await PrivateAxios.delete(`/admin/role/${roleId}/user/${userId}`);
                toast.success(`Rôle supprimé avec succès.`);

                setSelectedUser((prevUser) => {
                    const updatedRoles = { ...prevUser.roles };
                    delete updatedRoles[roleId];

                    return {
                        ...prevUser,
                        roles: updatedRoles
                    };
                });
            } catch (error) {
                toast.error('Erreur lors de la suppression du rôle.');
            }
        }
    };


    const deleteUser = async (userId) => {
        if (!window.confirm('Voulez-vous vraiment supprimer cette UG ?')) return;

        try {
            await PrivateAxios.delete(`/admin/supprimer/user/${userId}`);
            toast.success('Utilisateur supprimé avec succès !');
            fetchUsers();
        } catch (error) {
            toast.error('Erreur lors de la suppression de l\'utilisateur.');
        }
    };

    return (
        <Layout>
            <h1 className="mb-6 text-3xl font-bold text-center text-black">Modifier un utilisateur</h1>

            <div className="flex justify-center mb-4">
                <input
                    type="text"
                    placeholder="Rechercher par nom d'utilisateur"
                    className="w-1/3 p-2 border border-gray-300 rounded"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

            <div className="flex justify-center">
                <div className="w-full mx-auto overflow-x-auto text-center">
                    <table className="w-full bg-white border border-gray-200 rounded-lg shadow-md">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-2 py-2 border">Utilisateur</th>
                                <th className="px-2 py-2 border">Email</th>
                                <th className="px-2 py-2 border">UG</th>
                                <th className="px-2 py-2 border">Int</th>
                                <th className='px-2 py-2 border'>Rôles</th>
                                <th className="px-2 py-2 border">Actif</th>
                                <th className="px-2 py-2 border">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.length > 0 ? (
                                filteredUsers.map(user => (
                                    <tr key={user.id} className="hover:bg-gray-50">
                                        <td className="px-2 py-2 border">{user.username}</td>
                                        <td className="px-2 py-2 border">{user.email}</td>
                                        <td className="px-2 py-2 border">{user.ug_nom}</td>
                                        <td className="px-2 py-2 border">{user.intermediaire_nom}</td>
                                        <td className="px-2 py-2 border">
                                            {Object.values(user.roles).map(role => role.nom).join(', ')}
                                        </td>
                                        <td className="px-2 py-2 border">
                                            {user.actif ? (
                                                <span className="font-semibold text-green-600">Actif</span>
                                            ) : (
                                                <span className="font-semibold text-red-600">Inactif</span>
                                            )}
                                        </td>
                                        <td className="px-2 py-2 space-x-4 border">
                                            <button
                                                onClick={() => openPasswordModal(user)}
                                                className="text-blue-500 hover:text-blue-700"
                                                title="Changer le mot de passe"
                                            >
                                                <FaKey />
                                            </button>
                                            <button
                                                onClick={() => openEditModal(user)}
                                                className="text-yellow-500 hover:text-yellow-700"
                                                title="Modifier l'utilisateur"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                onClick={() => toggleUserStatus(user)}
                                                className={user.actif ? "text-green-500 hover:text-green-700" : "text-red-500 hover:text-red-700"}
                                                title={user.actif ? "Désactiver" : "Activer"}
                                            >
                                                {user.actif ? <FaLock /> : <FaUnlock />}
                                            </button>
                                            <button
                                                onClick={() => openRolesModal(user)}
                                                className="text-purple-500 hover:text-purple-700"
                                                title="Changer les droits"
                                            >
                                                <FaUserShield />
                                            </button>
                                            <button
                                                onClick={() => deleteUser(user.id)}
                                                className="text-red-500 hover:text-red-700"
                                                title="Supprimer un utilisateur"
                                            >
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="py-4 text-center">Aucun utilisateur trouvé.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modale pour modifier l'utilisateur */}
            <Modal
                isOpen={isModalOpen}
                onRequestClose={closeModal}
                contentLabel="Modifier Utilisateur"
                ariaHideApp={false}
                style={{
                    content: {
                        top: '50%',
                        left: '50%',
                        right: 'auto',
                        bottom: 'auto',
                        marginRight: '-50%',
                        transform: 'translate(-50%, -50%)',
                        width: '400px',
                        padding: '20px',
                        borderRadius: '10px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
                    }
                }}
            >
                <h2 className="mb-4 text-lg font-bold">Modifier l'utilisateur</h2>
                <form>
                    <div className="mb-4">
                        <label className="block mb-2 text-sm font-bold text-gray-700" htmlFor="username">
                            Nom d'utilisateur
                        </label>
                        <input
                            id="username"
                            type="text"
                            value={selectedUser ? selectedUser.username : ''}
                            onChange={(e) => setSelectedUser({ ...selectedUser, username: e.target.value })}
                            className="w-full px-3 py-2 transition-all duration-300 border rounded-lg shadow-sm focus:outline-none focus:ring focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block mb-2 text-sm font-bold text-gray-700" htmlFor="email">
                            Email
                        </label>
                        <input
                            id="email"
                            type="email"
                            value={selectedUser ? selectedUser.email : ''}
                            onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
                            className="w-full px-3 py-2 transition-all duration-300 border rounded-lg shadow-sm focus:outline-none focus:ring focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block mb-2 text-sm font-bold text-gray-700" htmlFor="intermediaireId">
                            Intermédiaire
                        </label>
                        {loadingIntermediaires ? (
                            <p className="text-gray-500">Chargement des intermédiaires...</p>
                        ) : (
                            <select
                                id="intermediaireId"
                                value={selectedUser ? selectedUser.intermediaire_id : ''}
                                onChange={(e) => setSelectedUser({ ...selectedUser, intermediaire_id: e.target.value })}
                                required
                                className="w-full px-3 py-2 transition-all duration-300 border rounded-lg shadow-sm focus:outline-none focus:ring focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Sélectionner un intermédiaire</option>
                                {intermediaires.map((intermediaire) => (
                                    <option key={intermediaire.id} value={intermediaire.id}>
                                        {intermediaire.nom} - UG {intermediaire.ug_nom}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    <div className="flex justify-end space-x-4">
                        <button
                            type="button"
                            onClick={closeModal}
                            className="px-4 py-2 bg-gray-300 rounded"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            className={`px-4 py-2 bg-blue-600 text-white rounded ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isSubmitting}
                            onClick={updateUser}
                        >
                            {isSubmitting ? 'En cours...' : 'Valider'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Modale pour changer le mot de passe */}
            <Modal
                isOpen={isPasswordModalOpen}
                onRequestClose={closePasswordModal}
                contentLabel="Modifier Mot de Passe"
                ariaHideApp={false}
                style={{
                    content: {
                        top: '50%',
                        left: '50%',
                        right: 'auto',
                        bottom: 'auto',
                        marginRight: '-50%',
                        transform: 'translate(-50%, -50%)',
                        width: '400px',
                        padding: '20px',
                        borderRadius: '10px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
                    }
                }}
            >
                <h2 className="mb-4 text-lg font-bold">Changer le mot de passe</h2>
                <form>
                    <div className="mb-4">
                        <label className="block mb-2 text-sm font-bold text-gray-700" htmlFor="password">
                            Nouveau mot de passe
                        </label>
                        <input
                            id="password"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full px-3 py-2 transition-all duration-300 border rounded-lg shadow-sm focus:outline-none focus:ring focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    <div className="flex justify-end space-x-4">
                        <button
                            type="button"
                            onClick={closePasswordModal}
                            className="px-4 py-2 bg-gray-300 rounded"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            className={`px-4 py-2 bg-blue-600 text-white rounded ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={isSubmitting}
                            onClick={updateUserPassword}
                        >
                            {isSubmitting ? 'En cours...' : 'Valider'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Modale pour changer les rôles */}
            <Modal
                isOpen={isRolesModalOpen}
                onRequestClose={closeRolesModal}
                contentLabel="Modifier Rôles"
                ariaHideApp={false}
                style={{
                    content: {
                        top: '50%',
                        left: '50%',
                        right: 'auto',
                        bottom: 'auto',
                        marginRight: '-50%',
                        transform: 'translate(-50%, -50%)',
                        width: '1000px', // largeur augmentée pour s'adapter à la grille
                        padding: '20px',
                        borderRadius: '10px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
                    }
                }}
            >
                <h2 className="mb-4 text-lg font-bold">
                    Modification des rôles - {selectedUser?.username}
                </h2>
                <form>
                    <div className="grid grid-cols-4 gap-6 mb-6">
                        {/* Rôles UG */}
                        <div>
                            <h3 className="mb-2 text-lg font-semibold">Rôles UG</h3>
                            {roles
                                .filter((role) => role.nom.toLowerCase().endsWith('_ug'))
                                .map((role) => (
                                    <div key={role.id} className="flex items-center mb-2">
                                        <input
                                            type="checkbox"
                                            id={`role-${role.id}`}
                                            checked={!!selectedUser?.roles[role.id]}
                                            onChange={(e) => handleRoleChange(role.id, e.target.checked)}
                                            className="mr-2 accent-axa-blue-400"
                                        />
                                        <label htmlFor={`role-${role.id}`} className="text-gray-900">
                                            {role.nom}
                                        </label>
                                    </div>
                                ))}
                        </div>

                        {/* Rôles INT */}
                        <div>
                            <h3 className="mb-2 text-lg font-semibold">Rôles INT</h3>
                            {roles
                                .filter((role) => role.nom.toLowerCase().endsWith('_int'))
                                .map((role) => (
                                    <div key={role.id} className="flex items-center mb-2">
                                        <input
                                            type="checkbox"
                                            id={`role-${role.id}`}
                                            checked={!!selectedUser?.roles[role.id]}
                                            onChange={(e) => handleRoleChange(role.id, e.target.checked)}
                                            className="mr-2 accent-axa-blue-400"
                                        />
                                        <label htmlFor={`role-${role.id}`} className="text-gray-900">
                                            {role.nom}
                                        </label>
                                    </div>
                                ))}
                        </div>

                        {/* Rôles Remise */}
                        <div>
                            <h3 className="mb-2 text-lg font-semibold">Rôles Remise</h3>
                            {roles
                                .filter((role) => role.nom.toLowerCase().startsWith('remise'))
                                .map((role) => (
                                    <div key={role.id} className="flex items-center mb-2">
                                        <input
                                            type="checkbox"
                                            id={`role-${role.id}`}
                                            checked={!!selectedUser?.roles[role.id]}
                                            onChange={(e) => handleRoleChange(role.id, e.target.checked)}
                                            className="mr-2 accent-axa-blue-400"
                                        />
                                        <label htmlFor={`role-${role.id}`} className="text-gray-900">
                                            {role.nom}
                                        </label>
                                    </div>
                                ))}
                        </div>

                        {/* Autres Rôles */}
                        <div>
                            <h3 className="mb-2 text-lg font-semibold">Autres Rôles</h3>
                            {roles
                                .filter(
                                    (role) =>
                                        !role.nom.toLowerCase().endsWith('_ug') &&
                                        !role.nom.toLowerCase().endsWith('_int') &&
                                        !role.nom.toLowerCase().startsWith('remise')
                                )
                                .map((role) => (
                                    <div key={role.id} className="flex items-center mb-2">
                                        <input
                                            type="checkbox"
                                            id={`role-${role.id}`}
                                            checked={!!selectedUser?.roles[role.id]}
                                            onChange={(e) => handleRoleChange(role.id, e.target.checked)}
                                            className="mr-2 accent-axa-blue-400"
                                        />
                                        <label htmlFor={`role-${role.id}`} className="text-gray-900">
                                            {role.nom}
                                        </label>
                                    </div>
                                ))}
                        </div>
                    </div>

                    <div className="flex justify-end space-x-4">
                        <button
                            type="button"
                            onClick={closeRolesModal}
                            className="px-4 py-2 bg-gray-300 rounded"
                        >
                            Fermer
                        </button>
                    </div>
                </form>
            </Modal>
        </Layout>
    );
};

export default ModifierUtilisateur;
